import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { LabelText } from '~/components/Fields/LabeledValue';
import { ApprovalStatusLabel } from '~/components/Structures/ApprovalMark';
import { menuProps } from '~/components/toolbar/OperationsToolbar/selectMenuProps';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import {
    domainEventBus,
    RegistrationApprovalStatusChangeRequestedEvent,
} from '~/domain/events';

interface RegistrationApprovalControlProps {
    sopInstanceUid: string;
    approvalStatus: DICOMApprovalStatus;
    labelId?: string;
}

const APPROVAL_MESSAGE_IDS: Record<DICOMApprovalStatus, string> = {
    [DICOMApprovalStatus.APPROVED]: 'approved',
    [DICOMApprovalStatus.REJECTED]: 'rejected',
    [DICOMApprovalStatus.UNAPPROVED]: 'unapproved',
};

export default function RegistrationApprovalControl({
    sopInstanceUid,
    approvalStatus,
    labelId = 'approvalStatus',
}: RegistrationApprovalControlProps) {
    const intl = useIntl();
    const labelElementId = useId();
    const [pendingStatus, setPendingStatus] =
        useState<DICOMApprovalStatus | null>(null);

    const allStatuses = Object.values(DICOMApprovalStatus);
    const currentStatus = allStatuses.includes(approvalStatus)
        ? approvalStatus
        : DICOMApprovalStatus.UNAPPROVED;

    const handleChange = (event: SelectChangeEvent<DICOMApprovalStatus>) => {
        const newStatus = event.target.value as DICOMApprovalStatus;
        if (newStatus !== approvalStatus) {
            setPendingStatus(newStatus);
        }
    };

    const confirm = () => {
        if (pendingStatus) {
            domainEventBus.publish(
                new RegistrationApprovalStatusChangeRequestedEvent(
                    sopInstanceUid,
                    pendingStatus
                )
            );
        }
        setPendingStatus(null);
    };

    return (
        <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
            <LabelText id={labelElementId}>
                <FormattedMessage id={labelId} />
            </LabelText>
            <Select
                size="small"
                fullWidth
                labelId={labelElementId}
                value={currentStatus}
                onChange={handleChange}
                data-testid="registration-approval-select"
                MenuProps={menuProps}
                renderValue={(selected) => (
                    <ApprovalStatusLabel status={selected} size={14} />
                )}
            >
                {/* Current status kept: MUI warns on a value with no option. */}
                {allStatuses.map((status) => (
                    <MenuItem key={status} value={status} sx={{ fontSize: 12 }}>
                        <ApprovalStatusLabel status={status} size={14} />
                    </MenuItem>
                ))}
            </Select>

            <ConfirmationDialog
                open={pendingStatus !== null}
                title={
                    <FormattedMessage id="registration.confirmApprovalStatusChange" />
                }
                message={
                    <FormattedMessage
                        id="registration.approvalStatusChangeDescription"
                        values={{
                            newApprovalStatus: pendingStatus
                                ? intl.formatMessage({
                                      id: APPROVAL_MESSAGE_IDS[pendingStatus],
                                  })
                                : '',
                        }}
                    />
                }
                onConfirm={confirm}
                onCancel={() => setPendingStatus(null)}
                confirmText={<FormattedMessage id="confirm" />}
                cancelText={<FormattedMessage id="cancel" />}
                mode="primary"
            />
        </Box>
    );
}
